<template>
  <div class="form tab-content">
    <input-field
      :label="$tc('Template', 1) + ' *'"
      :value="templateId"
      size="medium"
      inputType="other"
    >
      <treeselect
        :value="templateId"
        @input="updateTemplateID"
        :options="templateOptions"
        :load-options="loadTemplateOptions"
        :normalizer="normalizer"
        :disabled="disabled"
        class="form-input-select"
        disableFuzzyMatching
      />
    </input-field>

    <input-field label="" value="" size="small" inputType="other" />

    <input-field
      :label="$tc('Owner', 1)"
      :value="owner.selected"
      size="small"
      inputType="text"
      :field-disabled="true"
    />

    <input-field
      :label="$tc('Layer', 2)"
      :value="possibleLayers.names"
      size="small"
      inputType="text"
      :field-disabled="true"
    />

    <div
      class="input-field"
      style="justify-content: flex-end; height: 53px; margin-top: 8px"
    >
      <v-checkbox
        v-model="usedByNetwork"
        color="accent"
        class="multi-selection-checkbox"
        :ripple="false"
        :disabled="false"
        :label="$t('OnlyUsedElements')"
        @click="$emit('update-only-used-elements', usedByNetwork)"
      />
    </div>
  </div>
</template>

<script>
import InputField from "@/components/Home/Forms/_Molecules/InputField";
import { mapActions } from "vuex";
import { listsMixin } from "@/mixins/listsMixin";

export default {
  name: "AssetReservationFormTemplateTab",

  components: { InputField },

  mixins: [listsMixin],

  props: {
    disabled: {
      type: Boolean,
      required: true
    },

    templateId: {
      type: Number
    },

    actionType: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      templateOptions: null,
      owner: {
        options: null,
        selected: null
      },

      possibleLayers: {
        names: null,
        layers: null
      },

      /**
       * Indicates if only elements used by the network should be used
       */
      usedByNetwork: false,

      template: null
    };
  },

  computed: {
    /**
     * Indicates if all options are loaded
     * @returns {Boolean} indicating if all options are loaded
     */
    allOptionsLoaded() {
      return this.owner.options && this.templateOptions;
    }
  },

  watch: {
    /**
     * Watches for changes in the templateId value and gets the respective template
     * @param {String} newValue - the new template id
     */
    templateId(newValue) {
      this.getTemplate(newValue);
    },

    /**
     * Watches for changes in the allOptionsLoaded value
     * If they are and templateId has a valid value the template info is fetched
     * @param {Boolean} allLoaded - indicates if all options are loaded
     */
    allOptionsLoaded(allLoaded) {
      if (allLoaded && this.templateId) {
        this.getTemplate(this.templateId);
      }
    }
  },

  /**
   * Loads the owner options when the component is created
   */
  created() {
    this.loadOwnerOptions();
  },

  methods: {
    ...mapActions([
      "fetchAssetReservationTemplates",
      "fetchAssetReservationTemplateByUUID"
    ]),

    /**
     * Loads the template options
     */
    async loadTemplateOptions({ callback }) {
      try {
        let templates = await this.fetchAssetReservationTemplates();
        this.getValidTemplateOptions(templates);
      } catch (error) {
        console.log(error);
      }
      callback();
    },

    /**
     * Filters the templates so that only valid templates are shown to the user
     * @param {Array} templates - an array of templates
     */
    getValidTemplateOptions(templates) {
      if (this.actionType === "create" || this.actionType === "createAssetReservation") {
        // Valid layers - layers with objects that can have asset reservation that exist inside the selected area
        let validLayers = this.$store.getters
          .getValidAssetReservationObjectLayersInArea;

        this.templateOptions = templates.filter(
          template =>
            (template.art_accepted_layers === "all" ||
              validLayers.some(v =>
                template.art_accepted_layers.includes(v)
              )) &&
            template.art_active
        );

        if (
          this.templateId &&
          !this.templateOptions.find(
            template => template.art_id === this.templateId
          )
        ) {
          this.updateTemplateID(null);
        }
      } else {
        this.templateOptions = templates.filter(
          template => template.art_id === this.templateId
        );
      }
    },

    /**
     * Loads the owner options
     */
    loadOwnerOptions() {
      this.getList("list_organization", [], true).then(list => {
        this.owner.options = list;
      });
    },

    /**
     * Gets the name of the template owner
     * @param {Object} template - the selected template
     */
    getOwnerName(template) {
      let ownerID = null;
      let ownerName = null;

      if (template) {
        ownerID = template.art_owner_id;
        if (this.owner.options) {
          let owner = this.owner.options.find(item => item.or_id === ownerID);
          ownerName = this.getListElementLabel(owner);
          this.owner.selected = ownerName;
        }
      }
    },

    /**
     * Loads the template possible layers
     * @param {Object} template - the selected template
     */
    getPossibleLayers(template) {
      if (template) {
        let layers = template.art_accepted_layers;

        if (layers === "all") {
          this.possibleLayers.names = "All";
        } else {
          layers = layers.split(",");

          let layerNames = [];
          for (const layer of layers) {
            layerNames.push(this.$store.getters.getLayerTranslationKey(layer));
          }

          this.possibleLayers.names = layerNames.join(", ");
          this.possibleLayers.layers = layers;
        }
      } else {
        this.possibleLayers.names = null;
        this.possibleLayers.layers = null;
      }
    },

    /**
     * Normalizes the values of an option
     * @param {Object} option - a list option
     * @returns {Object} the normalized option
     */
    normalizer(option) {
      return {
        id: option.art_id,
        label: option.art_label
      };
    },

    /**
     * Updates the template ID
     */
    updateTemplateID(ID) {
      this.$emit("update-id", ID);
    },

    /**
     * Gets the template of the id
     * @param {String} id - the template id
     */
    async getTemplate(id) {
      let template = this.templateOptions.find(
        template => template.art_id === id
      );
      if (template && this.actionType === "create") {
        await this.getTemplateDefaultValues(template);
      } else {
        this.template = template;
      }

      this.getOwnerName(this.template);
      this.getPossibleLayers(this.template);
      this.$emit("update-template", this.template);
    },

    /**
     * Gets the template default values
     * @param {Object} template - the selected template
     */
    async getTemplateDefaultValues(template) {
      try {
        this.template = await this.fetchAssetReservationTemplateByUUID({
          templateUUID: template.art_uuid,
          appends: "request_fields"
        });
      } catch (e) {
        console.log(e);
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/AssetReservationForm/TemplateTab.vue